import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";

type Template = {
  id: string;
  staff_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
};

type Conflict = {
  appointment_id: string;
  patient_id: string;
  patient_name: string;
  start_datetime: string;
};

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function when(iso: string) {
  return new Date(iso).toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hhmm(value: string) {
  // A window that runs to the end of the day is held as the last instant of
  // it, because every comparison the server makes is on a plain time and
  // midnight written as 00:00 sorts before every start. Sliced to five
  // characters that reads "23:59", which is a quarter of an hour short of
  // what the doctor asked for and looks like the server rounded it down.
  if (value.startsWith("23:59:59")) return "midnight";
  return value.slice(0, 5);
}

/**
 * When a doctor works, and the days that differ.
 *
 * Two rules from the FSD shape this screen, and both are about not surprising
 * anybody.
 *
 * A window that overlaps one the doctor already holds is refused, at this clinic
 * or any other. One person cannot be bookable in two places, and finding that
 * out when a patient arrives is too late (SCH-F-002).
 *
 * Nothing here cancels a booking. Retiring a window or blocking a day voids the
 * open slots and hands back the bookings it covered, for a person to telephone.
 * A booked slot is somebody's morning, and a rule running at midnight does not
 * get to end it (SCH-F-004).
 */
export function Calendar() {
  const [rows, setRows] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);

  const [day, setDay] = useState(1);
  const [from, setFrom] = useState("09:00");
  const [to, setTo] = useState("13:00");
  const [minutes, setMinutes] = useState(15);

  const [blockDate, setBlockDate] = useState("");
  const [blockReason, setBlockReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await api.GET("/portal/availability/templates", {
      params: { header: clinicHeader(), query: {} },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data?.items ?? []) as Template[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addWindow(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setAddError(null);
    const { error: failed } = await api.POST("/portal/availability/templates", {
      params: { header: clinicHeader() },
      body: {
        day_of_week: day,
        start_time: from,
        end_time: to,
        slot_duration_minutes: minutes,
      } as never,
    });
    setBusy(false);
    if (failed) {
      // Beside the form, not at the top of the page. The commonest refusal
      // here is an overlap, and a doctor who has scrolled to the form to add
      // a window has scrolled past wherever a page-level error would sit.
      setAddError(readError(failed).message);
      return;
    }
    setAddError(null);
    await load();
  }

  async function retire(row: Template) {
    const label = `${DAYS[row.day_of_week]} ${hhmm(row.start_time)} to ${hhmm(row.end_time)}`;
    if (!window.confirm(`Stop using ${label}?\n\nBookings already made stay.`)) return;

    setBusy(true);
    const { data, error: failed } = await api.DELETE(
      "/portal/availability/templates/{id}",
      { params: { header: clinicHeader(), path: { id: row.id } } },
    );
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setConflicts((data?.conflicts ?? []) as Conflict[]);
    await load();
  }

  async function block(event: React.FormEvent) {
    event.preventDefault();
    if (!blockDate) return;

    setBusy(true);
    const { data, error: failed } = await api.POST("/portal/availability/exceptions", {
      params: { header: clinicHeader() },
      body: { date: blockDate, kind: "block", reason: blockReason } as never,
    });
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setConflicts((data?.conflicts ?? []) as Conflict[]);
    setBlockDate("");
    setBlockReason("");
  }

  return (
    <section>
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          <h1>My calendar</h1>
          <p className="screen-lead">
            The working windows that decide which slots patients can book.
          </p>
        </div>
      </header>
      <ErrorNote message={error} />

      {conflicts.length > 0 && (
        <div className="panel warn">
          <h2>These bookings are still standing</h2>
          <p>
            Nothing cancels a patient's appointment on its own. Telephone them,
            then move or cancel each one from the day's list.
          </p>
          <ul>
            {conflicts.map((row) => (
              <li key={row.appointment_id}>
                {row.patient_name}, {when(row.start_datetime)}
              </li>
            ))}
          </ul>
          <button type="button" className="link" onClick={() => setConflicts([])}>
            I have these
          </button>
        </div>
      )}

      <h2>Working windows</h2>
      {loading && rows.length === 0 && <p className="empty">Reading the calendar…</p>}
      {!loading && rows.length === 0 && (
        <p className="empty">
          No windows yet. Until one exists this clinic has nothing to book.
        </p>
      )}

      {rows.length > 0 && (
        <ol className="week">
          {DAYS.map((name, index) => {
            const onThisDay = rows
              .filter((row) => row.day_of_week === index)
              .sort((a, b) => a.start_time.localeCompare(b.start_time));
            return (
              <li key={name} className="week-col">
                <h3>{name}</h3>
                {onThisDay.length === 0 ? (
                  <p className="week-none">Not working</p>
                ) : (
                  onThisDay.map((row) => (
                    <div key={row.id} className="window">
                      <span className="window-time">
                        {hhmm(row.start_time)}
                        <span className="window-dash">–</span>
                        {hhmm(row.end_time)}
                      </span>
                      <span className="window-each">
                        {row.slot_duration_minutes} min each
                      </span>
                      <button
                        type="button"
                        className="link danger"
                        disabled={busy}
                        onClick={() => void retire(row)}
                      >
                        Stop using
                      </button>
                    </div>
                  ))
                )}
              </li>
            );
          })}
        </ol>
      )}

      <p className="week-note">
        These windows repeat every week until you change them.
      </p>

      <form className="panel" onSubmit={addWindow}>
        <h2>Add a window</h2>
        <p className="quiet">
          Mornings and evenings are two windows. A window with a gap in it is
          harder to read than two windows.
        </p>

        {/* The refusal has a home now. A doctor cannot be bookable in two
            places at once, at this clinic or any other, so the server rejects
            an overlapping window and the reason was landing at the top of the
            page the doctor had already scrolled past. */}
        {addError && (
          <div className="window-refused" role="alert">
            <strong>{addError}</strong>
            <p>
              A doctor cannot be bookable in two places at once, here or at
              another clinic. Choose a different time.
            </p>
          </div>
        )}

        <div className="row">
          <label className="field">
            Day
            <select value={day} onChange={(event) => setDay(Number(event.target.value))}>
              {DAYS.map((name, index) => (
                <option key={name} value={index}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            From
            <input
              type="time"
              value={from}
              required
              onChange={(event) => setFrom(event.target.value)}
            />
          </label>

          <label className="field">
            To
            <input
              type="time"
              value={to}
              required
              onChange={(event) => setTo(event.target.value)}
            />
          </label>

          <label className="field">
            Each appointment
            <select
              value={minutes}
              onChange={(event) => setMinutes(Number(event.target.value))}
            >
              {[10, 15, 20, 30].map((value) => (
                <option key={value} value={value}>
                  {value} minutes
                </option>
              ))}
            </select>
          </label>
        </div>

        <button type="submit" className="primary" disabled={busy}>
          Add it
        </button>
      </form>

      <form className="panel" onSubmit={block}>
        <h2>Block a day</h2>
        <p className="quiet">
          Leave, or a public holiday. It voids the open slots on that day and
          leaves the bookings alone.
        </p>

        <div className="row">
          <label className="field">
            Date
            <input
              type="date"
              value={blockDate}
              required
              onChange={(event) => setBlockDate(event.target.value)}
            />
          </label>

          <label className="field">
            Why
            <input
              type="text"
              value={blockReason}
              maxLength={200}
              placeholder="Annual leave"
              onChange={(event) => setBlockReason(event.target.value)}
            />
          </label>
        </div>

        <button type="submit" className="primary" disabled={busy || !blockDate}>
          Block it
        </button>
      </form>
    </section>
  );
}
